import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ListItemText
} from '@mui/material';
import {
  Google as GoogleIcon,
  Search as SearchIcon,
  FlagOutlined as FlagIcon,
  AssignmentOutlined as FormIcon,
  Link as LinkIcon,
  RemoveCircleOutline as DisconnectedIcon,
  CheckCircleOutline as ConnectedIcon,
  PeopleOutlined as PeopleIcon,
  DoneAll as DoneIcon,
  Sync as SyncIcon,
  SettingsOutlined as SettingsIcon
} from '@mui/icons-material';
import {
  getGoogleConfig,
  listGoogleAdsAccounts,
  connectGoogleLead,
  getGoogleLeadIntegrations,
  disconnectGoogleLead,
  syncGoogleLeadNow,
  updateGoogleLeadAssignment
} from '../../services/google';
import { getAssociates } from '../../services/associate';
import { leadService } from '../../services/lead';
import ConfirmModal from '../../components/ConfirmModal';

const Google = () => {
  const theme = useTheme();

  // Permanent and temporary connection states
  const [connectedIntegration, setConnectedIntegration] = useState(null);
  const [authorizedSession, setAuthorizedSession] = useState(null);

  // Configuration flow states
  const [loginUrl, setLoginUrl] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');

  // CRM Defaults & Round Robin Pool
  const [associates, setAssociates] = useState([]);
  const [selectedAssociates, setSelectedAssociates] = useState([]);
  const [crmDefaults, setCrmDefaults] = useState({
    defaultStatus: 'New',
    defaultStage: 'Lead',
    sourceLabel: 'Google Lead Ads',
    addressLine: '',
    city: '',
    state: ''
  });

  // UI States
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // Sync manual & Table states
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncedLeads, setSyncedLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [leadsPage, setLeadsPage] = useState(0);
  const [leadsRowsPerPage, setLeadsRowsPerPage] = useState(10);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const [updatingAssignment, setUpdatingAssignment] = useState(false);

  // Wizard steps
  const [activeStep, setActiveStep] = useState('A'); // A: Auth, B: Accounts, C: CRM Defaults, D: Connect

  // Fetch Associates for assignment pool
  const fetchAssociatesList = async () => {
    try {
      const res = await getAssociates({ page: 1, limit: 100 });
      let list = [];
      if (res && Array.isArray(res.data)) {
        list = res.data;
      } else if (res && Array.isArray(res.data?.docs)) {
        list = res.data.docs;
      }
      setAssociates(list.map(a => ({
        id: a._id || a.id,
        name: a.name || 'Unknown',
        number: a.associateId || ''
      })));
    } catch (err) {
      console.error('Failed to fetch associates:', err);
    }
  };

  const fetchSyncedLeads = async (customPage = leadsPage, customLimit = leadsRowsPerPage) => {
    setLeadsLoading(true);
    try {
      const leadsData = await leadService.getLeads({
        provider: 'google',
        page: customPage + 1,
        limit: customLimit
      });

      let leadsList = [];
      if (leadsData && Array.isArray(leadsData.docs)) {
        leadsList = leadsData.docs;
      } else if (Array.isArray(leadsData)) {
        leadsList = leadsData;
      } else if (leadsData && Array.isArray(leadsData.leads)) {
        leadsList = leadsData.leads;
      } else if (leadsData && Array.isArray(leadsData.data)) {
        leadsList = leadsData.data;
      }
      setSyncedLeads(leadsList);
      setTotalLeadsCount(leadsData?.totalDocs || leadsList.length || 0);
    } catch (err) {
      console.error('[Google Integration Error] Failed to fetch leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleChangeLeadsPage = (event, newPage) => {
    setLeadsPage(newPage);
  };

  const handleChangeLeadsRowsPerPage = (event) => {
    const newLimit = parseInt(event?.target?.value, 10);
    if (!isNaN(newLimit)) {
      setLeadsRowsPerPage(newLimit);
      setLeadsPage(0);
    }
  };

  useEffect(() => {
    if (connectedIntegration) {
      fetchSyncedLeads(leadsPage, leadsRowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsPage, leadsRowsPerPage]);

  const fetchGoogleIntegrations = async () => {
    setStatusLoading(true);
    try {
      // 1. Fetch Config
      const configRes = await getGoogleConfig();
      let foundUrl = '';
      if (configRes?.data?.authUrl) {
        foundUrl = configRes.data.authUrl;
      } else if (configRes?.authUrl) {
        foundUrl = configRes.authUrl;
      } else if (configRes?.data?.loginUrl) {
        foundUrl = configRes.data.loginUrl;
      } else if (configRes?.loginUrl) {
        foundUrl = configRes.loginUrl;
      }

      setLoginUrl(foundUrl);
      if (!foundUrl) {
        setError('Google login URL not configured. Please contact administrator.');
      }

      // 2. Fetch active integrations
      const statusRes = await getGoogleLeadIntegrations();
      const active = Array.isArray(statusRes) ? statusRes.find(i => i.status === 'connected' && i.isActive === true)
        : (statusRes?.data && Array.isArray(statusRes.data) ? statusRes.data.find(i => i.status === 'connected' && i.isActive === true) : null);

      setConnectedIntegration(active);
      if (active) {
        setSelectedAssociates(active.assignmentAssociateIds || active.assignmentPool || active.selectedAssociates || []);
        fetchSyncedLeads();
      }
    } catch (err) {
      console.error('fetchGoogleIntegrations failed:', err);
      setError(err.message || 'Failed to check active integration status.');
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchGoogleIntegrations();
    fetchAssociatesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Event listener for Google callback window oauth redirect
  useEffect(() => {
    const handleOAuthMessage = async (event) => {
      if (event.data?.type === 'google-oauth-callback') {
        const payload = event.data.payload || event.data;
        const code = payload.code;
        const state = payload.state;
        const redirectUri = payload.redirectUri || `${window.location.origin}/google-callback`;

        if (code) {
          setSuccess('Authorization successful! Fetching accessible Google Ads accounts...');
          setLoading(true);
          try {
            const accountsRes = await listGoogleAdsAccounts({ code, redirectUri, state });
            const list = accountsRes.data?.accessibleCustomers || accountsRes.accessibleCustomers || [];
            const grantId = accountsRes.data?.grantId || accountsRes.grantId;
            setAuthorizedSession({ code, redirectUri, state, grantId });
            setAccounts(list);
            setActiveStep('B');
          } catch (err) {
            setError(err.message || 'Failed to fetch Google Ads accounts.');
          } finally {
            setLoading(false);
          }
        }
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, []);

  const handleContinueWithGoogle = () => {
    if (!loginUrl) {
      setError('Google login URL not configured. Please contact administrator.');
      return;
    }

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      loginUrl,
      'Google Ads Authorization',
      `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`
    );
  };

  const handleConnectIntegration = async () => {
    if (!selectedAccount) {
      setError('Please select a Google Ads account first.');
      return;
    }

    // Find full selected account object to extract customerId and loginCustomerId
    const accObj = accounts.find(a => (a.resourceName || a.customerId || a.googleAdsCustomerId || a) === selectedAccount) || {};
    const customerId = accObj.customerId || accObj.googleAdsCustomerId || (typeof accObj === 'string' ? accObj : '');
    const loginCustomerId = accObj.loginCustomerId || '';

    setLoading(true);
    setError(null);
    try {
      const payload = {
        grantId: authorizedSession?.grantId,
        googleAdsCustomerId: customerId,
        loginCustomerId: loginCustomerId,
        defaultAssociateId: selectedAssociates[0] || '',
        assignmentAssociateIds: selectedAssociates,
        defaultInterestedIn: 'Plot',
        defaultStatus: crmDefaults.defaultStatus,
        defaultStage: crmDefaults.defaultStage,
        sourceLabel: crmDefaults.sourceLabel,
        defaultAddress: {
          addressLine: crmDefaults.addressLine,
          city: crmDefaults.city,
          state: crmDefaults.state
        },
        notes: 'Google Ads Campaign Lead Integration',
        initialSyncDays: 7
      };

      const res = await connectGoogleLead(payload);
      if (res) {
        setSuccess('Integration connected successfully! Real-time Google leads are now streaming.');
        setAuthorizedSession(null);
        setSelectedAccount('');
        setActiveStep('A');
        await fetchGoogleIntegrations();
      }
    } catch (err) {
      setError(err.message || 'Failed to connect integration.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAssignmentPool = async () => {
    const targetId = connectedIntegration?._id || connectedIntegration?.id;
    if (!targetId) return;

    setUpdatingAssignment(true);
    setError(null);
    setSuccess(null);
    try {
      await updateGoogleLeadAssignment(targetId, { assignmentAssociateIds: selectedAssociates });
      setSuccess('Lead assignment pool updated successfully.');
      await fetchGoogleIntegrations();
    } catch (err) {
      setError(err.message || 'Failed to update assignment pool.');
    } finally {
      setUpdatingAssignment(false);
    }
  };

  const handleSyncLeads = async () => {
    const targetId = connectedIntegration?._id || connectedIntegration?.id;
    if (!targetId) return;

    setSyncLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await syncGoogleLeadNow(targetId, {});
      setSuccess(res?.message || 'Sync triggered successfully!');
      await fetchSyncedLeads();
    } catch (err) {
      setError(err.message || 'Leads sync failed.');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDisconnect = () => {
    setConfirmOpen(true);
  };

  const executeDisconnect = async () => {
    setConfirmOpen(false);
    const targetId = connectedIntegration?._id || connectedIntegration?.id;
    if (!targetId) return;

    setLoading(true);
    setError(null);
    try {
      await disconnectGoogleLead(targetId);
      setSuccess('Google Lead Ads integration disconnected successfully.');
      setConnectedIntegration(null);
      setAuthorizedSession(null);
      await fetchGoogleIntegrations();
    } catch (err) {
      setError(err.message || 'Failed to disconnect integration.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAssociate = (associateId) => {
    setSelectedAssociates(prev =>
      prev.includes(associateId) ? prev.filter(id => id !== associateId) : [...prev, associateId]
    );
  };

  if (statusLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking integration status...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GoogleIcon sx={{ color: '#4285F4', fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>
            Google Lead Ads Integration
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => {
            fetchGoogleIntegrations();
            fetchSyncedLeads();
          }}
          disabled={statusLoading || leadsLoading}
          startIcon={<SyncIcon />}
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: 'white',
            borderColor: '#e2e8f0',
            color: '#475569',
            '&:hover': {
              bgcolor: '#f8fafc',
              borderColor: '#cbd5e1'
            }
          }}
        >
          Refresh Feed
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Connection Status Top Card */}
      <Card sx={{ borderRadius: 4, mb: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
        <CardContent sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
              {connectedIntegration ? 'Google Account Connected' : 'Connect Your Google Ads Account'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {connectedIntegration
                ? `Your Google Ads Account ID "${connectedIntegration.customerId || 'Connected Account'}" is authorized. Real-time leads sync is active.`
                : 'Authorize your Google Ads account to access lead forms and manage leads.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {connectedIntegration ? (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={loading}
                sx={{
                  borderRadius: 2.5,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Disconnect
              </Button>
            ) : authorizedSession ? (
              <Chip label="Authorized" color="success" sx={{ fontWeight: 800, py: 2.5, px: 2, borderRadius: 3 }} />
            ) : (
              <Button
                variant="contained"
                startIcon={<GoogleIcon />}
                onClick={handleContinueWithGoogle}
                sx={{
                  borderRadius: 2.5,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  backgroundColor: '#4285F4',
                  boxShadow: '0 8px 16px ' + alpha('#4285F4', 0.2),
                  '&:hover': {
                    backgroundColor: '#357ae8',
                    boxShadow: '0 8px 20px ' + alpha('#4285F4', 0.3)
                  }
                }}
              >
                Continue with Google
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Integration Setup Wizard Flow (Step B & C) */}
      {!connectedIntegration && authorizedSession && (
        <Card sx={{ borderRadius: 4, mb: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>
              Integration Settings Wizard
            </Typography>

            {activeStep === 'B' && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Step 1: Select Google Ads Account
                </Typography>
                <FormControl fullWidth sx={{ mb: 4 }}>
                  <InputLabel id="google-ads-account-label">Google Ads Account</InputLabel>
                  <Select
                    labelId="google-ads-account-label"
                    value={selectedAccount}
                    label="Google Ads Account"
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    sx={{ borderRadius: 3 }}
                  >
                    {accounts.map((acc, index) => {
                      const valueVal = acc.resourceName || acc.customerId || acc.googleAdsCustomerId || acc;
                      const labelVal = acc.descriptiveName || valueVal;
                      return (
                        <MenuItem key={valueVal + '-' + index} value={valueVal}>
                          {labelVal}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    disabled={!selectedAccount}
                    onClick={() => setActiveStep('C')}
                    sx={{ borderRadius: 2.5, px: 4, py: 1.2, textTransform: 'none', fontWeight: 700 }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

            {activeStep === 'C' && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3 }}>
                  Step 2: Configure CRM Defaults & Lead Assignment
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Source Label"
                      value={crmDefaults.sourceLabel}
                      onChange={(e) => setCrmDefaults({ ...crmDefaults, sourceLabel: e.target.value })}
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                      fullWidth
                      label="Default Stage"
                      value={crmDefaults.defaultStage}
                      onChange={(e) => setCrmDefaults({ ...crmDefaults, defaultStage: e.target.value })}
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                      fullWidth
                      label="Default Status"
                      value={crmDefaults.defaultStatus}
                      onChange={(e) => setCrmDefaults({ ...crmDefaults, defaultStatus: e.target.value })}
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Address Line"
                      value={crmDefaults.addressLine}
                      onChange={(e) => setCrmDefaults({ ...crmDefaults, addressLine: e.target.value })}
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                      fullWidth
                      label="City"
                      value={crmDefaults.city}
                      onChange={(e) => setCrmDefaults({ ...crmDefaults, city: e.target.value })}
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                    <TextField
                      fullWidth
                      label="State"
                      value={crmDefaults.state}
                      onChange={(e) => setCrmDefaults({ ...crmDefaults, state: e.target.value })}
                      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                      Lead Assignment (Round-Robin Pool)
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, maxHeight: '250px', overflowY: 'auto' }}>
                      <Grid container spacing={2}>
                        {associates.map((associate) => {
                          const isChecked = selectedAssociates.includes(associate.id);
                          return (
                            <Grid item xs={12} sm={6} md={4} key={associate.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                  checked={isChecked}
                                  onChange={() => handleToggleAssociate(associate.id)}
                                />
                                <Typography variant="body2">
                                  {associate.name} ({associate.number})
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep('B')}
                    sx={{ borderRadius: 2.5, px: 4, py: 1.2, textTransform: 'none', fontWeight: 700 }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={handleConnectIntegration}
                    sx={{ borderRadius: 2.5, px: 4, py: 1.2, textTransform: 'none', fontWeight: 700 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Connect Integration'}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Connected Configuration & Table view */}
      {connectedIntegration && (
        <>
          {/* Assignment Update & Manual Sync Cards */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Sync Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 3 }}>
                    <SyncIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Manual Leads Synchronization
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manually trigger lead import from Google Ads webhook integration now.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleSyncLeads}
                    disabled={syncLoading}
                    startIcon={syncLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                    sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 4, py: 1.5 }}
                  >
                    {syncLoading ? 'Syncing...' : 'Sync Leads Now'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Assignment Settings Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4, height: '100%', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 3 }}>
                    <SettingsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Lead Routing Round-Robin Pool
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select associates for incoming lead assignment.
                      </Typography>
                    </Box>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, maxHeight: '150px', overflowY: 'auto', mb: 3 }}>
                    <Grid container spacing={1}>
                      {associates.map((associate) => {
                        const isChecked = selectedAssociates.includes(associate.id);
                        return (
                          <Grid item xs={12} sm={6} key={associate.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handleToggleAssociate(associate.id)}
                              />
                              <Typography variant="body2">
                                {associate.name}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Paper>
                  <Button
                    variant="contained"
                    onClick={handleUpdateAssignmentPool}
                    disabled={updatingAssignment}
                    sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 4, py: 1.5 }}
                  >
                    {updatingAssignment ? <CircularProgress size={20} color="inherit" /> : 'Update Routing Pool'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Leads List Table */}
          <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'grey.100', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Recent Synced Google Leads
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Leads synchronized from Google Lead Ads campaigns.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<SyncIcon />}
                  onClick={() => fetchSyncedLeads()}
                  disabled={leadsLoading}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Refresh List
                </Button>
              </Box>

              {leadsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : syncedLeads.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Lead ID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Phone / Email</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Assignment Strategy</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {syncedLeads.map((lead) => {
                          const statusColors = (lead.status || '').toLowerCase() === 'new'
                            ? { bg: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }
                            : { bg: 'grey.100', color: 'text.secondary' };

                          return (
                            <TableRow key={lead._id || lead.id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {lead.leadId || lead._id || '—'}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {lead.customerDetails?.name || lead.name || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {lead.customerDetails?.phone || lead.phone || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {lead.customerDetails?.email || lead.email || ''}
                                </Typography>
                              </TableCell>
                              <TableCell>{lead.source || 'Google'}</TableCell>
                              <TableCell>
                                {lead.createdAt
                                  ? new Date(lead.createdAt).toLocaleString()
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {lead.assignmentInfo?.routingStrategy || 'Round-Robin'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={lead.status || 'New'}
                                  size="small"
                                  sx={{
                                    fontWeight: 800,
                                    bgcolor: statusColors.bg,
                                    color: statusColors.color
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    component="div"
                    count={totalLeadsCount}
                    rowsPerPage={leadsRowsPerPage}
                    page={leadsPage}
                    onPageChange={handleChangeLeadsPage}
                    onRowsPerPageChange={handleChangeLeadsRowsPerPage}
                  />
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body2" fontWeight={800} color="text.secondary" gutterBottom>
                    No synced leads found.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Disconnect confirmation modal */}
      <ConfirmModal
        open={confirmOpen}
        title="Disconnect Integration?"
        type="warning"
        confirmText="Disconnect"
        cancelText="Cancel"
        onClose={() => setConfirmOpen(false)}
        onConfirm={executeDisconnect}
      >
        Are you sure you want to disconnect your Google Lead Ads integration? Synced leads will remain, but real-time streaming will stop immediately.
      </ConfirmModal>
    </Box>
  );
};

export default Google;
